import gsap from 'gsap';
import { cleanup } from './dot-cms-aniamtion.helper';
import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';

const activeTweens = new WeakMap<Element, gsap.core.Tween | null>();
// Track active event listeners per element to ensure proper cleanup
const activeClickListeners = new WeakMap<Element, (event: Event) => void>();

// Helper function to remove existing click listener if it exists
function removeExistingClickListener(el: Element) {
  const existingListener = activeClickListeners.get(el);
  if (existingListener) {
    el.removeEventListener('click', existingListener);
    activeClickListeners.delete(el);
  }
}

// Helper function to add new click listener and track it
function addClickListener(el: Element, listener: (event: Event) => void) {
  removeExistingClickListener(el); // Remove any existing listener first
  el.addEventListener('click', listener);
  activeClickListeners.set(el, listener); // Track the new listener
}

export const clickAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween | null>
> = {
  'click-expand': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        scale: s.scale ?? 1.5,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            scale: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-shrink': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        scale: s.scale ? s.scale / 2 : 0.5,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            scale: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-move': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        x: s.angle ?? 100,
        y: s.angle ?? 0,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            x: 0,
            y: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-fade': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        opacity: s.opacity ?? 0,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            opacity: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-rotate': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        rotation: s.rotation ?? 360,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            rotation: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-spin': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;
    const spinAngle = s.direction === 'counter-clockwise' ? -360 * (s.spin_count ?? 1) : 360 * (s.spin_count ?? 1);

    function createTween() {
      return gsap.to(el, {
        rotation: spinAngle,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            rotation: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-skew': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        skewX: s.skew ?? 20,
        skewY: s.skew ?? 20,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            skewX: 0,
            skewY: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-color': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;
    const originalColor = window.getComputedStyle(el).color;

    function createTween() {
      return gsap.to(el, {
        color: s.color,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        overwrite: 'auto',
        delay: s.delay ?? 0,
        onComplete: () =>
          gsap.to(el, {
            color: originalColor,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-twist': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      // Reset rotation to 0 before starting the twist animation
      gsap.set(el, { rotation: 0 });

      return gsap.to(el, {
        rotation: s.angle ?? 45,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        delay: s.delay ?? 0,
        ...opts,
        onComplete: () => {
          gsap.to(el, { rotation: 0, duration: 0.3, overwrite: 'auto' });
        },
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-appear': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      // First, immediately set the element to the starting state (invisible)
      gsap.set(el, { opacity: 0 });

      return gsap.to(el, {
        opacity: 1,
        duration: s.duration ?? 0.6,
        ease: s.easing ?? 'power1.inOut',
        delay: s.delay ?? 0,
        ...opts,
        onComplete: () => {
          gsap.set(el, { opacity: 1 });
        },
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) return;
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },
};
